import { useEffect, useState } from "react";
import { sqlRead, payloadOf } from "../../utils/sqlClient";
import { ItemAssemblyPicker } from "../../components/ItemAssemblyPicker";

// Substitute an Item / Assembly with another across THIS estimate (Sage "Substitute Item"). Quantity, geometry and
// placement carry over; the replacement brings its own price, unit of measure, burden and markup. The server
// (sp_SubstituteEstimateItem, db/90) previews first — counts and warnings — and only then applies.
export function substitutionSummary(res) {
  if (!res) return [];
  const parts = [
    [res.DrawnRows, "drawn run / symbol"], [res.RoomElements, "room element"],
    [res.ScopeApplications, "scope placement"], [res.ManualLines, "manual line"],
  ].filter(([n]) => +n > 0).map(([n, label]) => `${n} ${label}${+n === 1 ? "" : "s"}`);
  return parts;
}

export default function SubstituteItemDialog({ old, revisionId, items, assemblies, categories, onClose, onDone }) {
  const [pick, setPick] = useState(null); // { kind, id, name, uom }
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const call = (preview) => sqlRead(`EXEC sp_SubstituteEstimateItem @payload = '${payloadOf({
    revision_id: revisionId, old_kind: old.kind, old_id: old.id, new_kind: pick.kind, new_id: pick.id, preview: preview ? 1 : 0,
  })}'`).then(r => r?.Table1?.[0]);

  useEffect(() => {
    if (!pick) return;
    let live = true;
    setPreview(null); setError(null);
    call(true).then(r => { if (live) setPreview(r); }).catch(e => { if (live) setError(e.message || String(e)); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pick?.kind, pick?.id]);

  async function apply() {
    setBusy(true); setError(null);
    try { await call(false); onDone(); } catch (e) { setError(e.message || String(e)); } finally { setBusy(false); }
  }

  const parts = substitutionSummary(preview);
  const nothing = preview && parts.length === 0;
  const activeOnly = (list) => list.filter(x => x.IsActive !== false && x.IsActive !== 0);
  return (
    <div role="dialog" aria-label="Substitute item" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "var(--surface)", color: "var(--text)", borderRadius: 8, padding: 18, width: 520, maxWidth: "92vw", boxShadow: "0 8px 30px rgba(0,0,0,0.3)" }}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Substitute item</div>
        <div style={{ fontSize: 12.5, color: "var(--text2)", marginBottom: 10 }}>
          Replace <b>{old.name}</b> ({old.uom || "—"}) with another catalog {old.kind === "assembly" ? "assembly or item" : "item or assembly"} everywhere in this estimate.
          Quantities and drawn geometry stay; the replacement uses its own price, unit and markup. The catalog is not changed.
        </div>
        <ItemAssemblyPicker
          items={activeOnly(items).filter(i => !(old.kind === "item" && i.ItemID === old.id))}
          assemblies={activeOnly(assemblies).filter(a => !(old.kind === "assembly" && a.AssemblyID === old.id))}
          categories={categories}
          value={pick ? { kind: pick.kind, id: pick.id } : null}
          onSelect={e => setPick({ kind: e.kind, id: e.id, name: e.name, uom: e.uom })}
        />
        {error && <div style={{ marginTop: 10, padding: "6px 8px", fontSize: 12, background: "var(--tint-red)", borderRadius: 4 }}>{error}</div>}
        {preview && !error && (
          <div style={{ marginTop: 10, fontSize: 12.5 }}>
            {nothing
              ? <div>Nothing in this estimate uses {old.name}.</div>
              : <div>This will move <b>{parts.join(", ")}</b> to <b>{preview.NewName}</b>.</div>}
            {!nothing && +preview.UomDiffers === 1 && (
              <div style={{ marginTop: 6, padding: "6px 8px", background: "var(--tint-amber)", borderRadius: 4 }}>
                Units differ: <b>{preview.OldUOM || "—"}</b> → <b>{preview.NewUOM || "—"}</b>. Quantities are kept as they are, not converted — check them after.
              </div>
            )}
            {!nothing && +preview.ManualLines > 0 && (
              <div style={{ marginTop: 6, color: "var(--text2)" }}>Manual lines lose their typed price, burden, markup and description (they described the old item).</div>
            )}
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
          <button onClick={onClose} style={{ fontSize: 12.5 }}>Cancel</button>
          <button onClick={apply} disabled={!preview || nothing || !!error || busy} style={{ fontSize: 12.5, fontWeight: 600, color: "var(--cs-blue)" }}>{busy ? "Substituting…" : "Substitute"}</button>
        </div>
        <div style={{ marginTop: 8, fontSize: 11, color: "var(--text3)" }}>AI-assisted draft — verify the replacement's price and quantities before bid use.</div>
      </div>
    </div>
  );
}
